/**
 * ViewSubscriptionModal.tsx - Subscription Details Modal
 * 
 * This modal displays detailed information about a subscription including
 * donor information, payment details, and subscription history.
 * 
 * Key Features:
 * - Complete subscription information display
 * - Payment method details
 * - Subscription status and next payment date
 * - Total amount paid and payment count
 * 
 * Props:
 * - isVisible: boolean - Controls modal visibility
 * - subscription: Subscription | null - Subscription data to display
 * - onClose: () => void - Close modal handler
 * 
 * Dependencies: Subscription type, styled components
 */

import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { X, RefreshCcw } from 'lucide-react';  
import { SubscriptionModel } from '../models';

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalTitleBar = styled.div`
  background: #EFFDF4;
  padding: 32px 32px 14px;
  text-align: center;
  border-radius: 24px 24px 0 0;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #10b981;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ContentContainer = styled.div`
  padding: 24px 40px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 12px 32px;
  margin-bottom: 20px;
  align-items: start;
`;

const DetailLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  text-align: left;
`;

const DetailValue = styled.div`
  font-size: 16px;
  color: #1e293b;
  font-weight: 500;
  line-height: 1.4;
  text-align: left;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => props.status === 'active' 
    ? 'background: #dcfce7; color: #166534;'
    : 'background: #fee2e2; color: #dc2626;'
  }
`;

const PaymentMethodContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaymentMethodIcon = styled.div<{ cardType?: string }>`
  width: 40px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  
  ${props => {
    switch (props.cardType) {
      case 'visa':
        return 'background: #1A1F71;';
      case 'mastercard':
        return 'background: #EB001B;';
      case 'amex':
        return 'background: #006FCF;';
      case 'discover':
        return 'background: #FF6000;';
      default:
        return 'background: #1A1F71;';
    }
  }}
`;

interface ViewSubscriptionModalProps {
  isVisible: boolean;
  subscription: SubscriptionModel | null;
  onClose: () => void;
}

export const ViewSubscriptionModal: React.FC<ViewSubscriptionModalProps> = ({
  isVisible,
  subscription,
  onClose
}) => {

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  // const renderPaymentMethod = () => {
  //   if (subscription?.paymentMethod?.type === 'card') {
  //     const cardName = subscription.paymentMethod.brand?.toUpperCase() || 'VISA';
  //     return (
  //       <PaymentMethodContainer>
  //         <PaymentMethodIcon cardType={subscription.paymentMethod.brand}>
  //           {cardName}
  //         </PaymentMethodIcon>
  //         <span>...{subscription.paymentMethod.last4}</span>
  //       </PaymentMethodContainer>
  //     );
  //   } else {
  //     return (
  //       <PaymentMethodContainer>
  //         <PaymentMethodIcon>
  //           ACH
  //         </PaymentMethodIcon>
  //         <span>...{subscription?.paymentMethod?.last6}</span>
  //       </PaymentMethodContainer>
  //     );
  //   }
  // };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isVisible, onClose]);

  if (!subscription || !isVisible) {
    return null;
  }

  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <X size={16} />
        </CloseButton>
        
        <ModalTitleBar>
          <ModalTitle>
            <RefreshCcw size={26} color="#10b981" />
            Subscription Details
          </ModalTitle>
        </ModalTitleBar>

        <ContentContainer>
          <DetailsGrid>
            <DetailLabel>Name</DetailLabel>
            <DetailValue>{subscription.getDisplayName()}</DetailValue>

            <DetailLabel>Email Address</DetailLabel>
            <DetailValue>{subscription.donorEmail || '–'}</DetailValue>

            <DetailLabel>Mobile Phone</DetailLabel>
            <DetailValue>{subscription.donorMobile || '–'}</DetailValue>

            <DetailLabel>Status</DetailLabel>
            <DetailValue>
              <StatusBadge status={subscription.status}>
                {subscription.status}
              </StatusBadge>
            </DetailValue>

            <DetailLabel>Monthly Amount</DetailLabel>
            <DetailValue>{formatCurrency(subscription.monthlyAmount)}</DetailValue>

            <DetailLabel>Next Due Date</DetailLabel>
            <DetailValue>{formatDate(subscription.nextDueDate)}</DetailValue>

            <DetailLabel>Total Paid</DetailLabel>
            <DetailValue>{formatCurrency(subscription.totalPaid)}</DetailValue>

            <DetailLabel>Payments</DetailLabel>
            <DetailValue>
              {subscription.paymentCount}{" "}
              {subscription.paymentCount === 1 ? "payment" : "payments"}
            </DetailValue>

            <DetailLabel>Start Date</DetailLabel>
            <DetailValue>{formatDate(subscription.startDate)}</DetailValue>

            {/* <DetailLabel>Payment Method</DetailLabel>
            <DetailValue>{renderPaymentMethod()}</DetailValue> */}
          </DetailsGrid>
        </ContentContainer>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};